// The project's Home (Console D, views/home.js; APS-305): does anything need me? The title is the
// verdict on the latest release in one sentence (D103 #14), the strip the numbers behind it, and
// "Needs you" one list of what needs a person, on one impact scale: alerts firing, new and
// regressed issues, the endpoint that got worse, missing symbols and pull requests waiting for
// review. Under it the latest crash as the orbit with its ephemeris; beside it crash-free sessions
// per day, adoption, and what agents did in the last 15 minutes or, when they did nothing, the
// slowest screens. With "All apps" and several apps, the apps side by side. It reads the bar's
// range and release.
import { useQueries, useQuery } from '@tanstack/react-query'
import { Link, useSearch } from '@tanstack/react-router'
import { type ComponentProps, type KeyboardEvent, type ReactNode, useRef, useState } from 'react'
import { notConfigured } from '../api/client'
import {
  agentSummaryQuery,
  alertDestinationsQuery,
  alertRulesQuery,
  appsQuery,
  compareQuery,
  dailyHealthQuery,
  endpointsQuery,
  fetchSessions,
  handoffsQuery,
  issuesQuery,
  lastDays,
  missingSymbolsQuery,
  releasesQuery,
  screensQuery,
  sessionQuery,
  useIssueHandoff,
  vitalsQuery,
} from '../api/queries'
import type {
  AgentSummary,
  App,
  EndpointSummary as Endpoint,
  IssueSummary,
  ReleaseHealth,
  ReleaseVitals,
} from '../api/types'
import {
  Btn,
  copyText,
  Kind,
  PageHead,
  Pill,
  ScrollX,
  Section,
  Status,
  Strip,
  type StripItem,
  useToast,
} from '../components/d'
import { Icon } from '../components/icons'
import { LineChart } from '../components/LineChart'
import { Orbit } from '../components/Orbit'
import { deviceMeta, LatencyText } from '../components/Perf'
import { appLabel } from '../components/Shell'
import { ErrorState, Loading } from '../components/states'
import { type MessageId, useI18n } from '../i18n'
import { type CallVerb, callWords } from '../lib/agentCall'
import { ruleSummary } from '../lib/alerts'
import { compact, duration, offset, percentSpaced, points, shortId } from '../lib/format'
import { bars, byImpact, type Need, perThousand } from '../lib/home'
import { issueTitle } from '../lib/issues'
import { ttidAt } from '../lib/latency'
import { appsHealth, firingRules, freezes } from '../lib/overview'
import { adoption, busiestApp, compare, previousOf, releasesNewestFirst } from '../lib/releases'
import { orbitFractions, sessionModel, type TimelineEvent } from '../lib/session'
import { sandboxFor } from '../sandbox'
import { useProjectParams } from './params'
import { pillTone, valueText } from './Session'
import { carried, deviceParams, rangeDays, type ScopeSearch } from './search'

/** How many issues of each kind "Needs you" asks for. */
const TOP = 6
/** The Agents panel reads the last 15 minutes, as the mock's (APS-387). */
const AGENTS_WINDOW_MS = 15 * 60_000

/** The latest crash's ephemeris shows this many events. */
const EPHEMERIS = 7

const HEADLINE = {
  worse: 'releases.headline.worse',
  better: 'releases.headline.better',
  same: 'releases.headline.same',
} as const

type Link_ = { to: string; params: Record<string, string>; search?: object }

export function HomePage() {
  const i18n = useI18n()
  const { t, number } = i18n
  const { orgId, projectId } = useProjectParams()
  const search = useSearch({ strict: false }) as ScopeSearch
  const sandbox = !!sandboxFor(projectId)
  const days = rangeDays(search.range, 'home')
  // The window is fixed per visit (rounded to the minute) so the query keys stay stable.
  const now = Math.floor(Date.now() / 60_000) * 60_000
  // The bar's device filters (APS-228) narrow every number of the page.
  const device = deviceParams(search)
  const window = useQuery(
    releasesQuery(projectId, { ...lastDays(days, now), ...device }, ['home', days, now, device]),
  )
  const lastDay = useQuery(
    releasesQuery(projectId, { ...lastDays(1, now), ...device }, ['home-day', now, device]),
  )
  const apps = useQuery(appsQuery(orgId, projectId))
  const health = window.data ? appsHealth(window.data, lastDay.data ?? []) : []
  const appId = search.app ?? (window.data ? busiestApp(window.data) : undefined)
  const app: App | undefined = apps.data?.find((a) => a.id === appId)

  // The release in view: the bar's, else the app's newest; the one before it to compare.
  const list = window.data && appId ? releasesNewestFirst(window.data, appId) : []
  const cur: ReleaseHealth | undefined = search.release
    ? list.find((r) => r.release === search.release)
    : list[0]
  const prev = cur ? previousOf(list, cur.release) : undefined
  const release = cur?.release

  const compared = useQuery({
    ...compareQuery(projectId, appId ?? '', prev?.release ?? '', release ?? '', device),
    enabled: !!prev && !!release && !!appId,
  })
  const cmp = cur ? compare(cur, prev, compared.data?.change) : undefined
  const [vCur, vPrev] = useQueries({
    queries: [
      {
        ...vitalsQuery(projectId, appId ?? '', release ?? '', device),
        enabled: !!release && !!appId,
      },
      {
        ...vitalsQuery(projectId, appId ?? '', prev?.release ?? '', device),
        enabled: !!prev && !!appId,
      },
    ],
  })
  const [eCur, ePrev] = useQueries({
    queries: [
      {
        ...endpointsQuery(projectId, { app_id: appId, release, days, ...device }, 'error_rate', 50),
        enabled: !!release,
      },
      {
        ...endpointsQuery(
          projectId,
          { app_id: appId, release: prev?.release, days, ...device },
          'error_rate',
          50,
        ),
        enabled: !!prev,
      },
    ],
  })
  const fresh = useQuery({
    ...issuesQuery(projectId, {
      app_id: appId,
      new_in: release,
      ...device,
      state: 'unresolved',
      sort: 'sessions',
      daily: true,
      days,
      limit: TOP,
    }),
    enabled: !!release,
  })
  const regressed = useQuery({
    ...issuesQuery(projectId, {
      app_id: appId,
      release,
      ...device,
      state: 'regressed',
      sort: 'sessions',
      daily: true,
      days,
      limit: TOP,
    }),
    enabled: !!release,
  })
  const missing = useQuery({
    ...missingSymbolsQuery(projectId, release ?? '', appId),
    enabled: !!release && !sandbox,
  })
  // Alert rules are the organization's: none in the sandbox (D64). Alerts switched off on the
  // server (503, not_configured) are no rows, not an error, as Settings › Alerts reads them.
  const rules = useQuery({ ...alertRulesQuery(orgId, projectId), enabled: !sandbox })
  const firing = firingRules(rules.data?.rules).filter((r) => !r.app_id || r.app_id === appId)
  const handoffs = useQuery({ ...handoffsQuery(orgId, projectId), enabled: !sandbox })
  const destinations = useQuery({ ...alertDestinationsQuery(orgId), enabled: !sandbox })
  const daily = useQuery({
    ...dailyHealthQuery(projectId, appId, new Date(now).toISOString(), device),
    enabled: !!appId,
  })
  const screens = useQuery({
    ...screensQuery(projectId, { app_id: appId, days, ...device }, 'ttid_p90', 5),
    enabled: !!appId,
  })
  // Agents over the last 15 minutes, as the mock's panel (APS-387): each client's last call in it.
  const agents = useQuery({
    ...agentSummaryQuery(orgId, projectId, 1),
    enabled: !sandbox,
    retry: false,
  })
  const recentAgents = (agents.data?.clients ?? [])
    .filter((c) => Date.parse(c.last_call.at) >= Date.now() - AGENTS_WINDOW_MS)
    .sort((a, b) => Date.parse(b.last_call.at) - Date.parse(a.last_call.at))
    .slice(0, 3)

  const params = { orgId, projectId }
  const scope = carried(search)
  const deviceText = deviceMeta(i18n, app, device)

  if (window.isPending)
    return (
      <main id="main" className="pg">
        <Loading />
      </main>
    )
  if (window.isError)
    return (
      <main id="main" className="pg">
        <PageHead title={t('nav.home')} />
        <ErrorState error={window.error} retry={() => window.refetch()} />
      </main>
    )
  // The bar's release had no sessions in the range: say so, with the app's newest to go to.
  if (!cur && search.release && list.length > 0)
    return (
      <main id="main" className="pg">
        <PageHead
          title={t('home.release.none', { release: search.release })}
          meta={t('home.meta.window', { days })}
          actions={
            <Link
              to="/o/$orgId/p/$projectId/home"
              params={params}
              search={{ ...scope, release: undefined }}
              className="btn"
            >
              {t('home.release.latest', { release: list[0]?.release ?? '' })}
            </Link>
          }
        />
      </main>
    )
  if (!cur || !appId || !cmp)
    return (
      <main id="main" className="pg">
        <PageHead
          title={t('home.empty.title')}
          meta={t('home.meta.window', { days })}
          actions={
            <Link
              to="/o/$orgId/p/$projectId/setup"
              params={params}
              search={scope}
              className="btn solid"
            >
              {t('overview.empty.action')}
            </Link>
          }
        />
        <p className="note">{t('overview.empty.body')}</p>
      </main>
    )

  // The verdict: the first sentence only (D103 #14). "Same" is the comparison's own threshold.
  const title =
    cmp.verdict === 'only'
      ? t('home.verdict.first', { release: cur.release })
      : t(HEADLINE[cmp.verdict], { release: cur.release, previous: prev?.release ?? '' })
  const appSessions = list.reduce((n, r) => n + r.sessions, 0)
  const shareOnCur = appSessions > 0 ? cur.sessions / appSessions : 0
  const k = cur.sessions // sessions on the release, the impact's base

  // ---- the strip ----
  const signed = (d: number, digits = 2) =>
    `${d < 0 ? '−' : '+'}${number(Math.abs(d), { minimumFractionDigits: digits, maximumFractionDigits: digits })}`
  const tone = (d: number | undefined, betterUp: boolean) =>
    d === undefined || Math.abs(d) < 0.005 ? '' : d > 0 === betterUp ? 'c-ok' : 'c-crash'
  const pct = (rate: number | null) =>
    rate === null ? '—' : number(rate * 100, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const vs = prev ? t('home.strip.vs', { release: prev.release }) : t('home.strip.first')
  const delta = (d: number | undefined, betterUp: boolean, text: string) =>
    d === undefined ? (
      vs
    ) : (
      <>
        <span className={tone(d, betterUp)}>{text}</span> {vs}
      </>
    )
  const sameTitle = t('home.strip.sameTitle', { points: number(0.01) })
  const coldCur = vCur?.data?.app_start.cold
  const coldPrev = vPrev?.data?.app_start.cold
  const coldDelta = coldCur && coldPrev ? coldCur.p90_ms - coldPrev.p90_ms : undefined
  const fz = freezes(app?.platform, vCur?.data)
  const fzPrev = freezes(app?.platform, vPrev?.data)
  const perK = (r: number | null | undefined) =>
    r === null || r === undefined ? undefined : r * 1000
  const fzK = perK(fz?.rate)
  const fzDelta =
    fzK !== undefined && perK(fzPrev?.rate) !== undefined
      ? fzK - (perK(fzPrev?.rate) as number)
      : undefined
  const totals = eCur?.data?.totals
  const requested = totals !== undefined && totals.requests > 0
  const worstEndpoint = (eCur?.data?.endpoints ?? []).find((e) => (e.error_rate ?? 0) > 0)
  const strip: StripItem[] = [
    {
      dt: t('releases.crashFreeSessions'),
      dd: pct(cur.crash_free_sessions_rate),
      small: '%',
      d: delta(
        cmp.sessionsDelta,
        true,
        t('home.strip.pts', { value: points(i18n, cmp.sessionsDelta ?? 0) }),
      ),
      link: (p) => (
        <Link
          to="/o/$orgId/p/$projectId/releases"
          params={params}
          search={scope}
          title={sameTitle}
          {...p}
        />
      ),
    },
    {
      dt: t('releases.crashFreeUsers'),
      dd: pct(cur.crash_free_users_rate),
      small: cur.crash_free_users_rate === null ? undefined : '%',
      d: delta(
        cmp.usersDelta,
        true,
        t('home.strip.pts', { value: points(i18n, cmp.usersDelta ?? 0) }),
      ),
      link: (p) => (
        <Link
          to="/o/$orgId/p/$projectId/releases"
          params={params}
          search={scope}
          title={sameTitle}
          {...p}
        />
      ),
    },
    {
      dt: t('home.strip.cold'),
      dd: coldCur ? number(Math.round(coldCur.p90_ms)) : '—',
      small: coldCur ? 'ms' : undefined,
      d: coldCur ? (
        <>
          {coldDelta !== undefined ? (
            <>
              <span className={tone(coldDelta, false)}>
                {t('home.strip.ms', { value: signed(coldDelta, 0) })}
              </span>
              {' · '}
            </>
          ) : null}
          {t('vitals.p50', { value: duration(i18n, coldCur.p50_ms) })}
        </>
      ) : (
        t('overview.noVitals')
      ),
      link: (p) => (
        <Link to="/o/$orgId/p/$projectId/performance/start" params={params} search={scope} {...p} />
      ),
    },
    {
      dt:
        fz?.kind === 'anr' || app?.platform === 'android'
          ? t('home.strip.anr')
          : t('home.strip.hang'),
      dd: fzK === undefined ? '—' : number(fzK, { maximumFractionDigits: 2 }),
      small: fzK === undefined ? undefined : t('home.strip.perK'),
      d:
        fzDelta === undefined ? (
          t('home.strip.perKSessions')
        ) : (
          <>
            <span className={tone(fzDelta, false)}>{signed(fzDelta)}</span>{' '}
            {t('home.strip.perKSessions')}
          </>
        ),
      link: (p) => (
        <Link to="/o/$orgId/p/$projectId/performance/hangs" params={params} search={scope} {...p} />
      ),
    },
    {
      dt: t('home.strip.failed'),
      // A dash only when no request was captured; captured requests that never failed read 0 %.
      dd: requested ? number((totals.error_rate ?? 0) * 100, { maximumFractionDigits: 1 }) : '—',
      small: requested ? '%' : undefined,
      d: !requested ? (
        t('home.strip.noRequests')
      ) : worstEndpoint ? (
        <span translate="no">
          {`${worstEndpoint.method} ${worstEndpoint.template} ${number((worstEndpoint.error_rate ?? 0) * 100, { maximumFractionDigits: 1 })} %`}
        </span>
      ) : (
        t('home.strip.noFailures')
      ),
      link: (p) => (
        <Link
          to="/o/$orgId/p/$projectId/performance/network"
          params={params}
          search={scope}
          {...p}
        />
      ),
    },
    {
      dt: t('nav.sessions'),
      dd: compact(i18n, appSessions),
      d: (
        <span translate="no">
          {t('home.strip.onRelease', { count: compact(i18n, cur.sessions), release: cur.release })}
        </span>
      ),
      link: (p) => (
        <Link to="/o/$orgId/p/$projectId/sessions" params={params} search={scope} {...p} />
      ),
    },
  ]

  // ---- Needs you ----
  const needs: (Need & { go: Link_; action?: ReactNode })[] = []
  const issueLink = (id: string): Link_ => ({
    to: '/o/$orgId/p/$projectId/issues/$issueId',
    params: { ...params, issueId: id },
    search: scope,
  })
  const handoffDest = (destinations.data?.destinations ?? []).find(
    (d) => (d.kind === 'github' || d.kind === 'linear') && d.handoff_enabled_at,
  )
  const handedOff = new Set((handoffs.data?.handoffs ?? []).map((h) => h.issue_id).filter(Boolean))
  for (const r of firing) {
    const crashRule = r.preset === 'crash_free_below' || r.preset === 'store_crash_rate'
    const anrRule = r.preset === 'store_anr_rate'
    const value = crashRule
      ? perThousand(
          cur.sessions - Math.round(cur.sessions * cur.crash_free_sessions_rate),
          cur.sessions,
        )
      : anrRule
        ? fzK
        : undefined
    needs.push({
      key: `alert:${r.id}`,
      kind: 'alert',
      status: 'fire',
      word: 'alert',
      title: ruleSummary(r, i18n),
      sub: t('home.need.firingSince', { when: i18n.relative(r.state_since) }),
      perK: value,
      figure: crashRule
        ? [`${pct(cur.crash_free_sessions_rate)} %`, t('home.need.alert')]
        : [t('home.need.alert'), ''],
      go: {
        to: '/o/$orgId/p/$projectId/alerts/$ruleId',
        params: { ...params, ruleId: r.id },
      },
      // Row actions at rest (D121): the rule, where it can be muted or changed.
      action: (
        <Link
          className="btn sm"
          to="/o/$orgId/p/$projectId/alerts/$ruleId"
          params={{ ...params, ruleId: r.id }}
        >
          {t('home.need.openRule')}
        </Link>
      ),
    })
  }
  const issueNeed = (i: IssueSummary, status: 'new' | 'reg'): void => {
    const sub = [
      i.message && i.message !== i.type ? i.message : '',
      status === 'reg' && i.state?.regressed_release
        ? t('issues.state.regressedIn', { release: i.state.regressed_release })
        : '',
    ]
      .filter(Boolean)
      .join(' · ')
    needs.push({
      key: `issue:${i.id}`,
      kind: 'issue',
      status,
      word: i.kind,
      title: issueTitle(i),
      sub: sub || shortId(i.id, 8, 4),
      perK: perThousand(i.sessions, k),
      spark: i.daily?.sessions.slice(-14),
      figure: [
        t('home.need.sessions', { count: i.sessions }),
        t('home.need.users', { count: i.users }),
      ],
      go: issueLink(i.id),
      ...(handoffDest && !handedOff.has(i.id) && !sandbox
        ? {
            action: (
              <HandOff
                orgId={orgId}
                projectId={projectId}
                issueId={i.id}
                destinationId={handoffDest.id}
              />
            ),
          }
        : {}),
    })
  }
  for (const i of fresh.data?.issues ?? []) issueNeed(i, 'new')
  for (const i of regressed.data?.issues ?? [])
    if (!needs.some((n) => n.key === `issue:${i.id}`)) issueNeed(i, 'reg')
  const worse = worseEndpoint(eCur?.data?.endpoints, ePrev?.data?.endpoints)
  if (worse) {
    const failed = Math.round(worse.now.requests * (worse.now.error_rate ?? 0))
    needs.push({
      key: `endpoint:${worse.now.method} ${worse.now.host}${worse.now.template}`,
      kind: 'endpoint',
      status: 'new',
      word: 'network',
      title: t('home.need.endpoint', {
        endpoint: `${worse.now.method} ${worse.now.template}`,
        rate: number((worse.now.error_rate ?? 0) * 100, { maximumFractionDigits: 1 }),
      }),
      sub: t('home.need.endpointSub', {
        before: number((worse.before ?? 0) * 100, { maximumFractionDigits: 1 }),
        release: prev?.release ?? '',
        host: worse.now.host,
      }),
      perK: perThousand(failed, k),
      spark: worse.now.daily?.error_rate.map((v: number | null) => v ?? 0).slice(-14),
      figure: [
        t('home.need.ofRequests', {
          failed: compact(i18n, failed),
          requests: compact(i18n, worse.now.requests),
        }),
        t('home.need.requests'),
      ],
      go: {
        to: '/o/$orgId/p/$projectId/performance/network/endpoint',
        params,
        search: {
          ...scope,
          app: worse.now.app_id,
          method: worse.now.method,
          host: worse.now.host,
          template: worse.now.template,
        },
      },
    })
  }
  const missingImages = missing.data?.missing ?? []
  if (missingImages.length > 0) {
    const ids = new Set(missingImages.flatMap((m) => m.issues))
    const affected = [...(fresh.data?.issues ?? []), ...(regressed.data?.issues ?? [])].filter(
      (i) => ids.has(i.id),
    )
    const sessions = affected.reduce((n, i) => n + i.sessions, 0)
    needs.push({
      key: 'symbols',
      kind: 'symbols',
      status: 'miss',
      word: 'symbols',
      title: t('issues.foot.symbols', { count: missingImages.length, release: cur.release }),
      sub: [...new Set(missingImages.map((m) => m.image_name))].slice(0, 3).join(', '),
      perK: sessions > 0 ? perThousand(sessions, k) : undefined,
      // No issue names an image yet: the images, not a false "0 issues".
      figure:
        ids.size > 0
          ? [t('home.need.issues', { count: ids.size }), t('home.need.affected')]
          : [t('home.need.images', { count: missingImages.length }), t('home.need.noSymbols')],
      go: {
        to: '/o/$orgId/p/$projectId/settings/$section',
        params: { ...params, section: 'symbols' },
      },
      action: (
        <Link
          className="btn sm"
          to="/o/$orgId/p/$projectId/settings/$section"
          params={{ ...params, section: 'symbols' }}
        >
          <Icon name="download" small />
          {t('home.need.upload')}
        </Link>
      ),
    })
  }
  for (const h of handoffs.data?.handoffs ?? []) {
    const pr = h.pull_request
    if (!pr || (pr.state !== 'draft' && pr.state !== 'in_review') || !h.issue_id) continue
    const issue = [...(fresh.data?.issues ?? []), ...(regressed.data?.issues ?? [])].find(
      (i) => i.id === h.issue_id,
    )
    needs.push({
      key: `review:${h.id}`,
      kind: 'review',
      status: 'reg',
      statusLabel: t('home.need.review'),
      word: 'review',
      title: t('home.need.pr', { number: pr.number, subject: h.subject }),
      sub: t('home.need.prSub', { issue: shortId(h.issue_id, 8, 4), ref: h.external_ref ?? '' }),
      perK: issue ? perThousand(issue.sessions, k) : undefined,
      spark: issue?.daily?.sessions.slice(-14),
      figure: [
        pr.state === 'draft' ? t('home.need.draft') : t('home.need.inReview'),
        `#${pr.number}`,
      ],
      go: issueLink(h.issue_id),
      action: (
        <a className="btn sm" href={pr.url} target="_blank" rel="noreferrer">
          {t('home.need.reviewPr')}
        </a>
      ),
    })
  }
  const ordered = byImpact(needs) as typeof needs
  const scale = bars(ordered)
  const needsLoading = fresh.isPending || regressed.isPending
  const shipDay = (daily.data ?? []).findIndex(
    (d) => d.day.slice(0, 10) === cur.released_at.slice(0, 10),
  )

  // ---- the side ----
  const crashRule = firingRules(rules.data?.rules)
    .concat((rules.data?.rules ?? []).filter((r) => r.enabled && r.state !== 'firing'))
    .find((r) => r.preset === 'crash_free_below' && (!r.app_id || r.app_id === appId))
  const threshold = crashRule ? Number(crashRule.params.threshold_percent) / 100 : undefined
  const adopt = adoption((lastDay.data ?? []).filter((r) => r.app_id === appId)).slice(0, 3)
  const copyAgent = [
    prev
      ? `apsio releases compare "${prev.release}" "${cur.release}" --app ${appId}`
      : `apsio releases --app ${appId}`,
  ].join('\n')

  return (
    <main id="main" className="pg">
      <PageHead
        variant="say"
        title={title}
        meta={
          <>
            {app ? <span translate="no">{appLabel(app)}</span> : null}
            {app ? ' · ' : null}
            {t('home.meta.share', {
              share: percentSpaced(i18n, shareOnCur, 0),
              release: cur.release,
            })}
            {' · '}
            {t('home.meta.window', { days })}
            {deviceText ? (
              <>
                {' · '}
                <span translate="no">{deviceText}</span>
              </>
            ) : null}
          </>
        }
        actions={<HeadActions copy={copyAgent} params={params} scope={scope} />}
      />
      <Strip items={strip} label={t('overview.kpis')} />

      {health.length > 1 && !search.app ? (
        <AppsSideBySide health={health} apps={apps.data ?? []} />
      ) : null}

      {/* What needs you, full width (brand v2, design/v2/console Home). */}
      <Section
        title={t('home.needs')}
        meta={needsLoading ? undefined : t('home.needs.meta', { count: ordered.length })}
        right={
          <Link
            className="btn ghost sm"
            to="/o/$orgId/p/$projectId/issues"
            params={params}
            search={scope}
          >
            {t('overview.allIssues')}
          </Link>
        }
      >
        {needsLoading ? (
          <Loading />
        ) : fresh.isError ? (
          <ErrorState error={fresh.error} retry={() => fresh.refetch()} />
        ) : ordered.length === 0 ? (
          <p className="note">{t('home.needs.none', { release: cur.release })}</p>
        ) : (
          <NeedsList
            needs={ordered}
            scale={scale}
            release={cur.release}
            shipDay={
              shipDay >= 0 ? shipDay - Math.max(0, (daily.data?.length ?? 0) - 14) : undefined
            }
          />
        )}
        {rules.isError && !notConfigured(rules.error) ? (
          <ErrorState error={rules.error} retry={() => rules.refetch()} />
        ) : null}
      </Section>
      <div className="hm-grid">
        <div className="hm-main">
          <LatestCrash appId={appId} days={days} release={search.release} build={search.build} />
        </div>
        <div className="hm-side">
          <Section
            title={t('releases.crashFreeSessions')}
            meta={t('home.chart.meta')}
            right={
              <Link
                className="btn ghost sm"
                to="/o/$orgId/p/$projectId/releases"
                params={params}
                search={scope}
              >
                {t('nav.releases')}
              </Link>
            }
          >
            {daily.isPending ? (
              <Loading />
            ) : daily.isError ? (
              <ErrorState error={daily.error} retry={() => daily.refetch()} />
            ) : (
              <LineChart
                label={t('home.chart.label', { release: cur.release })}
                height={190}
                points={(daily.data ?? []).map((p) => ({
                  label: i18n.date(`${p.day.slice(0, 10)}T00:00:00Z`, {
                    month: 'short',
                    day: 'numeric',
                    timeZone: 'UTC',
                  }),
                  value: p.crash_free_sessions_rate,
                }))}
                // The unit is in the head ("daily · %"), as on the mock: the axis and the last point
                // print the number alone.
                format={(v) => pct(v)}
                marker={shipDay >= 0 ? { index: shipDay, label: cur.release } : undefined}
                threshold={
                  threshold !== undefined && Number.isFinite(threshold)
                    ? { value: threshold, label: t('home.chart.alert') }
                    : undefined
                }
              />
            )}
          </Section>
          <div className="hm-side2">
            {adopt.length > 0 ? (
              <Section title={t('overview.adoption')} meta={t('home.adoption.meta')}>
                {adopt.map((a, n) => (
                  <div key={a.release} className={n === 0 ? 'hm-adopt' : 'hm-adopt o'}>
                    <span translate="no">{a.release}</span>
                    <span className="b" aria-hidden="true">
                      <i style={{ width: `${Math.round(a.share * 100)}%` }} />
                    </span>
                    <span>{percentSpaced(i18n, a.share, 0)}</span>
                  </div>
                ))}
              </Section>
            ) : null}
            {recentAgents.length > 0 ? (
              <Section
                title={t('nav.agents')}
                meta={t('home.agents.meta')}
                right={
                  <Link
                    className="btn ghost sm"
                    to="/o/$orgId/p/$projectId/agents"
                    params={params}
                    search={scope}
                  >
                    {t('home.agents.all')}
                  </Link>
                }
              >
                <ul className="hm-feed">
                  {recentAgents.map((c) => (
                    <li key={`${c.kind}:${c.id}`}>
                      <span className="fi" aria-hidden="true">
                        <Icon name="agent" small />
                      </span>
                      <AgentCall client={c.label ?? c.name ?? shortId(c.id)} call={c.last_call} />
                      <time dateTime={c.last_call.at}>
                        {i18n.relative(c.last_call.at, undefined, 'compact')}
                      </time>
                    </li>
                  ))}
                </ul>
              </Section>
            ) : screens.data && screens.data.length > 0 ? (
              <Section
                title={t('overview.screens')}
                meta={t('home.screens.meta')}
                right={
                  <Link
                    className="btn ghost sm"
                    to="/o/$orgId/p/$projectId/performance/screens"
                    params={params}
                    search={scope}
                  >
                    {t('overview.allScreens')}
                  </Link>
                }
              >
                <ul className="hm-screens">
                  {screens.data.map((s) => (
                    <li key={`${s.app_id}:${s.screen}`}>
                      <Link
                        to="/o/$orgId/p/$projectId/performance/screens/detail"
                        params={params}
                        search={{ ...scope, screen: s.screen }}
                        translate="no"
                      >
                        {s.screen}
                      </Link>
                      <span>
                        <LatencyText v={ttidAt(s, 'p90_ms')} />
                      </span>
                    </li>
                  ))}
                </ul>
              </Section>
            ) : null}
          </div>
        </div>
      </div>
    </main>
  )
}

/** The endpoint whose failure rate rose the most from the release before (enough requests). */
function worseEndpoint(
  now: Endpoint[] | undefined,
  before: Endpoint[] | undefined,
): { now: Endpoint; before: number | null } | undefined {
  if (!now || !before) return undefined
  const key = (e: Endpoint) => `${e.method} ${e.host}${e.template}`
  const was = new Map(before.map((e) => [key(e), e.error_rate ?? 0]))
  let best: { now: Endpoint; before: number | null; rise: number } | undefined
  for (const e of now) {
    if (e.requests < 20 || e.error_rate === null) continue
    const b = was.get(key(e)) ?? 0
    const rise = e.error_rate - b
    if (rise > 0.005 && (!best || rise > best.rise)) best = { now: e, before: b, rise }
  }
  return best ? { now: best.now, before: best.before } : undefined
}

function HeadActions({
  copy,
  params,
  scope,
}: {
  copy: string
  params: { orgId: string; projectId: string }
  scope: ScopeSearch
}) {
  const { t } = useI18n()
  const { toast, show } = useToast()
  return (
    <>
      <Link className="btn" to="/o/$orgId/p/$projectId/releases" params={params} search={scope}>
        {t('home.compare')}
      </Link>
      <Btn
        onClick={async () =>
          show((await copyText(copy)) ? t('issues.copied') : t('issues.copyFailed'))
        }
      >
        <Icon name="agent" />
        {t('issues.copyForAgent')}
      </Btn>
      {toast}
    </>
  )
}

function HandOff({
  orgId,
  projectId,
  issueId,
  destinationId,
}: {
  orgId: string
  projectId: string
  issueId: string
  destinationId: string
}) {
  const { t } = useI18n()
  const handoff = useIssueHandoff(orgId, projectId)
  return (
    <button
      type="button"
      className="btn sm"
      disabled={handoff.isPending || handoff.isSuccess}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        handoff.mutate({ issueId, destinationId })
      }}
    >
      {handoff.isSuccess ? t('home.need.handedOff') : t('home.need.handOff')}
    </button>
  )
}

/** A 64 × 20 sparkline with the release's ship day as a dotted hairline and the last point. */
function HmSpark({ values, shipDay }: { values: readonly number[]; shipDay?: number | undefined }) {
  const n = values.length
  if (n < 2) return <span className="hm-sp" />
  const max = Math.max(...values, 0.0001)
  const x = (i: number) => 1 + (i / (n - 1)) * 62
  const y = (v: number) => 18 - (v / max) * 15
  return (
    <svg className="hm-sp" viewBox="0 0 64 20" aria-hidden="true" focusable="false">
      {shipDay !== undefined && shipDay >= 0 && shipDay < n ? (
        <line x1={x(shipDay)} x2={x(shipDay)} y1="1" y2="19" />
      ) : null}
      <polyline points={values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ')} />
      <circle cx={x(n - 1)} cy={y(values[n - 1] ?? 0)} r="1.8" />
    </svg>
  )
}

/** "Needs you": one row per item, on one impact scale; j/k move, Enter opens. */
function NeedsList({
  needs,
  scale,
  release,
  shipDay,
}: {
  needs: (Need & { go: Link_; action?: ReactNode })[]
  scale: Map<string, { share: number; over: boolean } | undefined>
  release: string
  shipDay: number | undefined
}) {
  const { t, number } = useI18n()
  const per = (v: number) => number(v, { maximumFractionDigits: v < 10 ? 2 : 1 })
  const [cursor, setCursor] = useState(0)
  const rows = useRef<(HTMLAnchorElement | null)[]>([])
  const move = (to: number) => {
    const i = Math.max(0, Math.min(needs.length - 1, to))
    setCursor(i)
    rows.current[i]?.focus()
  }
  const onKey = (e: KeyboardEvent) => {
    if (e.metaKey || e.ctrlKey || e.altKey) return
    if (e.key === 'j') {
      e.preventDefault()
      move(cursor + 1)
    } else if (e.key === 'k') {
      e.preventDefault()
      move(cursor - 1)
    }
  }
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: j/k move between the list's links
    <div className="hm-inbox" onKeyDown={onKey}>
      <div className="hm-hd" aria-hidden="true">
        <span>{t('home.col.state')}</span>
        <span>{t('home.col.what')}</span>
        <span title={t('home.col.perKTitle', { release })}>{t('home.col.perK')}</span>
        <span>
          {t('home.col.days')}
          <i className="hm-mk" />
          <span translate="no">{release}</span>
        </span>
        <span className="r">{t('home.col.count')}</span>
      </div>
      <ul className="hm-list" aria-label={t('home.needs')}>
        {needs.map((n, i) => {
          const bar = scale.get(n.key)
          return (
            <li key={n.key} className={i === cursor ? 'hm-row cur' : 'hm-row'}>
              <Link
                {...(n.go as unknown as ComponentProps<typeof Link>)}
                ref={(el: HTMLAnchorElement | null) => {
                  rows.current[i] = el
                }}
                className="hm-i"
                tabIndex={i === cursor ? 0 : -1}
                onFocus={() => setCursor(i)}
              >
                <span className="w">
                  <Status s={n.status} {...(n.statusLabel ? { label: n.statusLabel } : {})} />
                  <Kind k={n.word} />
                </span>
                <span className="tt">
                  <b translate="no" title={n.title}>
                    {n.title}
                  </b>
                  <span title={n.sub}>{n.sub}</span>
                </span>
                {/* The bar, and its per-1k figure beside it so the scale never hides the number. */}
                <span className="hm-imc">
                  {bar ? (
                    <span className={bar.over ? 'hm-im over' : 'hm-im'} aria-hidden="true">
                      <i style={{ width: `${(bar.share * 100).toFixed(1)}%` }} />
                    </span>
                  ) : (
                    <span className="hm-im none" aria-hidden="true" />
                  )}
                  {n.perK !== undefined ? (
                    <span
                      className="hm-imv"
                      title={t('home.col.perKValue', { value: per(n.perK), release })}
                    >
                      {per(n.perK)}
                      <span className="visually-hidden">
                        {' '}
                        {t('home.col.perKValue', { value: per(n.perK), release })}
                      </span>
                    </span>
                  ) : null}
                </span>
                {n.spark && n.spark.length > 1 ? (
                  <HmSpark values={n.spark} shipDay={shipDay} />
                ) : (
                  <span className="hm-sp" />
                )}
                <span className="hm-fg">
                  <b>{n.figure[0]}</b>
                  <span>{n.figure[1]}</span>
                </span>
              </Link>
              {n.action ? <span className="ac">{n.action}</span> : null}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/** The latest crash: the session as the orbit around the device, with its ephemeris. */
function LatestCrash({
  appId,
  days,
  release,
  build,
}: {
  appId: string
  days: number
  release: string | undefined
  /** Which builds (APS-508): the bar's, release ones by default. */
  build: ScopeSearch['build']
}) {
  const i18n = useI18n()
  const { t } = i18n
  const { orgId, projectId } = useProjectParams()
  const params = { orgId, projectId }
  const latest = useQuery({
    queryKey: ['project', projectId, 'home', 'latest-crash', appId, release, days, build],
    queryFn: () =>
      fetchSessions(projectId, {
        app_id: appId,
        release,
        build,
        outcome: 'crashed',
        days: Math.min(30, Math.max(1, Math.ceil(days))),
        limit: 1,
      }),
  })
  const id = latest.data?.sessions[0]?.session_id
  const session = useQuery({ ...sessionQuery(projectId, id ?? ''), enabled: !!id })
  if (latest.isPending || (id && session.isPending)) return null
  if (!id || !session.data) return null
  const model = sessionModel(session.data)
  const fractions = orbitFractions(model)
  const shown = model.ephemeris
  const at = model.periapsis
  const picked = shown[at]
  const span = shown.at(-1)?.at ?? 0
  const hangs = shown
    .map((e, i) => ({ e, i }))
    .filter(({ e }) => e.signal === 'hang')
    .map(({ e, i }) => ({
      fraction: fractions[i] ?? 0,
      endFraction: span > 0 ? Math.min(1, (e.at + e.duration) / span) : 1,
    }))
  const sessionLink = {
    to: '/o/$orgId/p/$projectId/sessions/$sessionId' as const,
    params: { ...params, sessionId: id },
  }
  // The ephemeris around the crash: the last EPHEMERIS events up to and with it.
  const from = Math.max(0, at - EPHEMERIS + 1)
  const rows = shown.slice(from, at + 1)
  return (
    <Section
      title={t('home.crash')}
      meta={
        <span translate="no">
          {t('home.crash.meta', {
            session: shortId(id, 4, 4),
            when: i18n.relative(latest.data?.sessions[0]?.last_seen_at ?? ''),
          })}
        </span>
      }
      right={
        <Link className="btn ghost sm" {...sessionLink}>
          {t('home.crash.open')}
        </Link>
      }
    >
      <div className="hm-crash">
        {/* The orbit is a picture with words in it, so the link's name is its own hidden text, not
            an aria-label the picture's words would have to start (WCAG 2.5.3, axe 4.14). */}
        <Link className="orbit hm-orb" {...sessionLink}>
          <span className="vh">{t('home.crash.openLabel', { session: shortId(id, 4, 4) })}</span>
          <Orbit
            ticks={fractions.map((f) => ({ fraction: f }))}
            hangs={hangs}
            periapsis={at}
            selected={at}
            deviceLabel={t('session.device')}
            {...(picked
              ? {
                  centerTitle: offset(i18n, Math.max(0, picked.at)),
                  centerSub: [picked.name, picked.sub].filter(Boolean).join(' · '),
                }
              : {})}
          />
        </Link>
        <ol
          className="hm-eph"
          aria-label={t('home.crash.ephemeris', { session: shortId(id, 4, 4) })}
        >
          <li className="hm-eh" aria-hidden="true">
            <span>#</span>
            <span>{t('home.crash.t')}</span>
            <span>{t('home.crash.signal')}</span>
            <span>{t('home.crash.value')}</span>
          </li>
          {rows.map((e, n) => (
            <li key={e.id} className={from + n === at ? 'on' : undefined}>
              <Link {...sessionLink}>
                <span className="n">{from + n + 1}</span>
                <span className="t">{offset(i18n, Math.max(0, e.at))}</span>
                <span className="s" translate="no">
                  {e.name} <span>{e.sub}</span>
                </span>
                <span className="v">
                  <EphValue e={e} />
                </span>
              </Link>
            </li>
          ))}
        </ol>
      </div>
    </Section>
  )
}

const CALL_VERB: Record<CallVerb, MessageId> = {
  read: 'home.agents.verb.read',
  created: 'home.agents.verb.created',
  changed: 'home.agents.verb.changed',
  deleted: 'home.agents.verb.deleted',
}

/**
 * An agent's last call on one line (APS-387): its MCP tool as code, or, called directly, the
 * route in product words ("read releases"), or a short route; the full one in the title.
 */
function AgentCall({
  client,
  call,
}: {
  client: string
  call: AgentSummary['clients'][number]['last_call']
}) {
  const i18n = useI18n()
  const code = (text: string) => <span translate="no">{text}</span>
  const words = call.tool ? null : callWords(call.route)
  return (
    <span className="hm-call" title={call.tool ?? call.route}>
      {words?.kind === 'words'
        ? i18n.rich('home.agents.did', {
            client,
            verb: i18n.t(CALL_VERB[words.verb]),
            what: code(words.what),
          })
        : i18n.rich('home.agents.ran', {
            client,
            tool: code(call.tool ?? (words?.kind === 'route' ? words.short : call.route)),
          })}
    </span>
  )
}

/** An ephemeris value: a crash or a warning as its tinted pill, as on the mock; others plain. */
function EphValue({ e }: { e: TimelineEvent }) {
  const i18n = useI18n()
  const value = valueText(i18n, e)
  const tone = pillTone(e)
  return value && tone ? <Pill tone={tone}>{value}</Pill> : value
}

/** Each app at its latest release, when the scope is every app and there are several. */
function AppsSideBySide({ health, apps }: { health: ReturnType<typeof appsHealth>; apps: App[] }) {
  const i18n = useI18n()
  const { t, number } = i18n
  const { orgId, projectId } = useProjectParams()
  const device = deviceParams(useSearch({ strict: false }) as ScopeSearch)
  const vitals = useQueries({
    queries: health.map((h) => vitalsQuery(projectId, h.appId, h.latest.release, device)),
  })
  const cold = (v: ReleaseVitals | null | undefined) =>
    v?.app_start.cold ? duration(i18n, v.app_start.cold.p90_ms) : '—'
  // Per 1k sessions, as the strip says it (D103 #14).
  const rate = (r: number | null | undefined) =>
    r === null || r === undefined ? '—' : number(r * 1000, { maximumFractionDigits: 2 })
  return (
    <Section title={t('overview.apps')} meta={t('overview.appsSub')}>
      <ScrollX label={t('overview.appsTable')}>
        <table className="t">
          <caption className="visually-hidden">{t('overview.appsTable')}</caption>
          <thead>
            <tr>
              <th scope="col" className="f">
                {t('overview.col.app')}
              </th>
              <th scope="col">{t('overview.col.release')}</th>
              <th scope="col" className="num">
                {t('releases.crashFreeSessions')}
              </th>
              <th scope="col" className="num">
                {t('vitals.cold')}
              </th>
              <th scope="col" className="num">
                {t('home.col.freezesPerK')}
              </th>
              <th scope="col" className="num last">
                {t('overview.adoption')}
              </th>
            </tr>
          </thead>
          <tbody>
            {health.map((h, n) => {
              const a = apps.find((x) => x.id === h.appId)
              const v = vitals[n]?.data
              return (
                <tr key={h.appId}>
                  <td className="f">
                    <Link
                      to="/o/$orgId/p/$projectId/home"
                      params={{ orgId, projectId }}
                      search={{ app: h.appId }}
                      className="ttl"
                    >
                      {a ? appLabel(a) : h.appId}
                    </Link>
                  </td>
                  <td translate="no">{h.latest.release}</td>
                  <td className="num">
                    {h.latest.crash_free_sessions_rate === null
                      ? '—'
                      : percentSpaced(i18n, h.latest.crash_free_sessions_rate, 2)}
                  </td>
                  <td className="num">{cold(v)}</td>
                  <td className="num">{rate(freezes(a?.platform, v)?.rate)}</td>
                  <td className="num last">
                    {h.adoption === undefined ? '—' : percentSpaced(i18n, h.adoption, 0)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </ScrollX>
    </Section>
  )
}
